import {
  useState, useEffect, useCallback, useRef,
} from 'react';

import qapi from 'views_shared/app/javascript/api';
import { MESSAGES_PAGE_SIZE } from 'views_shared/app/javascript/api/messages';
import qlog from 'views_shared/app/javascript/scripts/qlog';
import generateUuid from 'views_shared/app/javascript/utils/generateUuid';

// Backend max for plain text; also drives the composer's character counter.
export const MAX_MESSAGE_LENGTH = 5000;

// Plain-text length of the composer's HTML — what the backend limit applies to.
export const strippedMessageLength = (html) => (html || '').replace(/<[^>]*>/g, '').trim().length;

// Merge freshly-fetched messages into the loaded list: drop optimistic placeholders,
// dedupe on message_id (server rows win), keep oldest-to-newest order.
// If a server message looks like the replacement for a pending optimistic (same sender,
// same attachment count), carry `pending: true` onto the real attachments so Attachment.jsx
// keeps showing "Uploading..." until the signed-URL fetch resolves (avoids an Uploading->Loading flicker).
function mergeMessages(existing, incoming) {
  const byMessageId = new Map();
  const pendingOptimistics = [];
  existing.forEach((message) => {
    if (String(message.message_id).startsWith('optimistic-')) {
      if (message.message_attachments && message.message_attachments.length > 0) {
        pendingOptimistics.push(message);
      }
    } else {
      byMessageId.set(message.message_id, message);
    }
  });
  // Only the latest attachment-bearing message per sender is eligible to "claim" a pending
  // optimistic — older messages happen to share sender + attachment count and would otherwise
  // be marked Uploading instead of the actual just-sent row.
  const latestAttIdxBySender = new Map();
  incoming.forEach((message, idx) => {
    const atts = message.message_attachments;
    if (!atts || atts.length === 0) return;
    const sender = message.sender_profile_slug;
    const prevIdx = latestAttIdxBySender.get(sender);
    if (prevIdx === undefined || incoming[prevIdx].sent_at < message.sent_at) {
      latestAttIdxBySender.set(sender, idx);
    }
  });
  incoming.forEach((message, msgIdx) => {
    let next = message;
    const atts = message.message_attachments;
    if (atts && atts.length > 0 && latestAttIdxBySender.get(message.sender_profile_slug) === msgIdx) {
      const matchIdx = pendingOptimistics.findIndex(
        (o) => o.sender_profile_slug === message.sender_profile_slug
          && o.message_attachments.length === atts.length,
      );
      if (matchIdx >= 0) {
        pendingOptimistics.splice(matchIdx, 1);
        next = { ...message, message_attachments: atts.map((a) => ({ ...a, pending: true })) };
      }
    }
    byMessageId.set(next.message_id, next);
  });
  return Array.from(byMessageId.values()).sort((a, b) => {
    if (a.sent_at !== b.sent_at) return a.sent_at < b.sent_at ? -1 : 1;
    return a.message_id < b.message_id ? -1 : 1;
  });
}

/**
 * Hook for managing message state, fetching, and sending.
 *
 * Owns:
 * - messages array (paginated: newest page on open, older pages prepended via loadOlder)
 * - messageContent state for the composer
 * - sendMessage function (POST to /replies)
 *
 * @param {string|null} conversationId - The active conversation's UUID
 * @param {string} currentReceiverProfileId - The current user's profile slug
 * @param {function} [onConversationUpdate] - Called after a message is sent (e.g., to refresh the conversations list)
 * @returns {object}
 */
function useMessages(
  conversationId,
  currentReceiverProfileId,
  onConversationUpdate,
  sendOverride,
  initialComposerState = {},
) {
  const [messages, setMessages] = useState([]);
  const [messageContent, setMessageContent] = useState(initialComposerState.messageContent || '');
  const [attachments, setAttachments] = useState([]);
  // The "Request a video" toggle: on = this send is a video request (stamped into message
  // metadata server-side). Pure intent — the message text plays no part.
  const [videoRequestIntent, setVideoRequestIntent] = useState(!!initialComposerState.videoRequestIntent);
  // idle | sending | failed -- drives the composer's attachment items while a reply with files is in flight.
  const [sendState, setSendState] = useState('idle');
  // Synchronous re-entrancy guard: sendState alone can't stop a double-click — the second
  // click lands before React re-renders the disabled button (verified double-send).
  const sendingRef = useRef(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const [hasMoreOlder, setHasMoreOlder] = useState(false);

  // Guard against stale responses when user switches conversations during a fetch
  const activeConversationIdRef = useRef(conversationId);
  activeConversationIdRef.current = conversationId;

  // Refs let loadOlder read current state without a stale closure (keeps the callback stable)
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const hasMoreOlderRef = useRef(hasMoreOlder);
  hasMoreOlderRef.current = hasMoreOlder;
  const isLoadingOlderRef = useRef(false);

  // Scroll intent for Messages: false | "instant" (conversation change) | "smooth" (sent message)
  const pendingScrollRef = useRef(false);

  // --- Fetch the newest page (mount / conversation change) ---
  const fetchMessages = useCallback(
    async ({ conversationChange } = {}) => {
      if (!conversationId) {
        setMessages([]);
        setHasMoreOlder(false);
        return;
      }

      if (conversationChange) {
        pendingScrollRef.current = 'instant';
        setHasMoreOlder(false);
      }

      setIsLoading(true);
      try {
        const { data, error } = await qapi.messages.list(conversationId, currentReceiverProfileId);

        // Discard if user switched conversations while we were fetching
        if (activeConversationIdRef.current !== conversationId) return;

        if (error) {
          qlog.error('Failed to fetch messages:', error);
          return;
        }

        const rows = data || [];
        setMessages(rows);
        setHasMoreOlder(rows.length === MESSAGES_PAGE_SIZE);
      } catch (err) {
        qlog.error('Failed to fetch messages:', err);
      } finally {
        // Only clear loading if this completion is for the still-active conversation.
        if (activeConversationIdRef.current === conversationId) {
          setIsLoading(false);
        }
      }
    },
    [conversationId, currentReceiverProfileId],
  );

  // Fetch on conversation change
  useEffect(() => {
    fetchMessages({ conversationChange: true });
  }, [fetchMessages]);

  // --- Load the next older page and prepend it (scroll-up infinite scroll) ---
  const loadOlder = useCallback(async () => {
    if (!conversationId || isLoadingOlderRef.current || !hasMoreOlderRef.current) return;

    // messages[0] is always the oldest loaded real message — optimistic ones append at the end.
    const oldest = messagesRef.current[0];
    if (!oldest) return;

    isLoadingOlderRef.current = true;
    setIsLoadingOlder(true);
    try {
      const { data, error } = await qapi.messages.list(conversationId, currentReceiverProfileId, {
        cursor: { sentAt: oldest.sent_at, messageId: oldest.message_id },
      });

      if (activeConversationIdRef.current !== conversationId) return;

      if (error) {
        qlog.error('Failed to load older messages:', error);
        return;
      }

      const rows = data || [];
      if (rows.length > 0) {
        setMessages((prev) => [...rows, ...prev]);
      }
      setHasMoreOlder(rows.length === MESSAGES_PAGE_SIZE);
    } catch (err) {
      qlog.error('Failed to load older messages:', err);
    } finally {
      setIsLoadingOlder(false);
      isLoadingOlderRef.current = false;
    }
  }, [conversationId, currentReceiverProfileId]);

  // --- Realtime: merge the newest page in without disturbing loaded older pages ---
  const syncLatestMessages = useCallback(async () => {
    if (!conversationId) return;
    try {
      const { data, error } = await qapi.messages.list(conversationId, currentReceiverProfileId);

      if (activeConversationIdRef.current !== conversationId) return;

      if (error) {
        qlog.error('Failed to sync latest messages:', error);
        return;
      }

      const rows = data || [];
      if (rows.length === 0) return;
      setMessages((prev) => mergeMessages(prev, rows));
    } catch (err) {
      qlog.error('Failed to sync latest messages:', err);
    }
  }, [conversationId, currentReceiverProfileId]);

  // --- Send message (optimistic) ---
  const sendMessage = useCallback(async () => {
    const strippedText = messageContent.replace(/<[^>]*>/g, '').trim();
    if (!strippedText && attachments.length === 0) return;
    if (!conversationId && !sendOverride) return;

    if (strippedText.length > MAX_MESSAGE_LENGTH) {
      globalThis.Qwoted.showToast({
        toastTitle: 'Message too long',
        toastBody: `Message exceeds 5,000 character limit (${strippedText.length} characters)`,
        variant: 'warning',
      });
      return;
    }

    if (sendingRef.current) return;
    sendingRef.current = true;
    setSendState('sending');
    try {
      // Override transport (no conversation yet): no optimistic row — the surface swaps to
      // the real conversation on success; composer state is kept on failure for retry.
      if (sendOverride) {
        const { error } = await Promise.resolve(
          sendOverride({ body: messageContent, attachments, videoRequest: videoRequestIntent }),
        ).catch((e) => ({ error: e }));

        if (error) {
          qlog.error('Failed to send message:', error);
          globalThis.Qwoted.showToast({
            toastTitle: 'Failed to send',
            toastBody: 'Your message could not be sent. Please try again.',
            variant: 'danger',
          });
          return;
        }

        setMessageContent('');
        setAttachments([]);
        setVideoRequestIntent(false);
        return;
      }

      const savedContent = messageContent;
      const savedAttachments = attachments;

      if (savedAttachments.length > 0) {
        const optimisticId = `optimistic-${generateUuid()}`;
        const optimisticAttachments = savedAttachments.map((a, i) => ({
          id: `${optimisticId}-att-${i}`,
          pending: true,
          filename: a.filename,
        }));
        const optimisticMessage = {
          id: optimisticId,
          message_id: optimisticId,
          sender_profile_id: globalThis.Qwoted.CURRENT_PROFILE_ID,
          sender_profile_slug: globalThis.Qwoted.CURRENT_SLUG,
          body: messageContent,
          content_format: 'html',
          sent_at: new Date().toISOString(),
          message_attachments: optimisticAttachments,
        };
        const previousMessages = messages;

        setMessages((prev) => [...prev, optimisticMessage]);
        pendingScrollRef.current = 'smooth';
        setMessageContent('');
        setAttachments([]);
        setVideoRequestIntent(false);

        // Network failures (ERR_NETWORK_CHANGED, offline) reject rather than returning { error };
        // normalize so the rollback + toast path always runs.
        const { error } = await qapi.messages
          .sendReply(conversationId, savedContent, savedAttachments, { videoRequest: videoRequestIntent })
          .catch((e) => ({ error: e }));

        if (error) {
          qlog.error('Failed to send message:', error);
          setMessages(previousMessages);
          setMessageContent(savedContent);
          setAttachments(savedAttachments);
          globalThis.Qwoted.showToast({
            toastTitle: 'Failed to send',
            toastBody: 'Your message could not be sent. Please try again.',
            variant: 'danger',
          });
          return;
        }

        onConversationUpdate?.(conversationId);
        // Pull the real message + persisted attachment IDs so Attachment swaps the inline data URI
        // for the signed Cloudinary URL.
        syncLatestMessages();
        return;
      }

      // Text-only: show the message immediately (optimistic) and reconcile on the realtime round-trip.
      const optimisticId = `optimistic-${generateUuid()}`;
      const optimisticMessage = {
        id: optimisticId,
        message_id: optimisticId,
        sender_profile_id: globalThis.Qwoted.CURRENT_PROFILE_ID,
        sender_profile_slug: globalThis.Qwoted.CURRENT_SLUG,
        body: messageContent,
        content_format: 'html',
        sent_at: new Date().toISOString(),
      };
      const previousMessages = messages;

      setMessages((prev) => [...prev, optimisticMessage]);
      pendingScrollRef.current = 'smooth';
      setMessageContent('');
      setVideoRequestIntent(false);

      const { error } = await qapi.messages
        .sendReply(conversationId, savedContent, [], { videoRequest: videoRequestIntent })
        .catch((e) => ({ error: e }));

      if (error) {
        qlog.error('Failed to send message:', error);
        // Rollback: remove optimistic message, restore composer
        setMessages(previousMessages);
        setMessageContent(savedContent);
        globalThis.Qwoted.showToast({
          toastTitle: 'Failed to send',
          toastBody: 'Your message could not be sent. Please try again.',
          variant: 'danger',
        });
        return;
      }

      onConversationUpdate?.(conversationId);
    } finally {
      sendingRef.current = false;
      setSendState('idle');
    }
  }, [
    conversationId,
    messageContent,
    attachments,
    messages,
    onConversationUpdate,
    syncLatestMessages,
    videoRequestIntent, sendOverride,
  ]);

  const refetchMessages = useCallback(() => fetchMessages(), [fetchMessages]);

  return {
    messages,
    messageContent,
    setMessageContent,
    attachments,
    setAttachments,
    videoRequestIntent,
    setVideoRequestIntent,
    sendState,
    sendMessage,
    isLoading,
    isLoadingOlder,
    hasMoreOlder,
    loadOlder,
    syncLatestMessages,
    pendingScrollRef,
    refetchMessages,
  };
}

export default useMessages;
